import { useState } from 'react';
import { useQueries } from 'react-query';
import { useSelector } from 'react-redux';
import { getAsmCertPaged, getCertCounter, getCertList } from 'services/asm-service';
import { useAuthTenant } from 'store/auth';
import { DefaultPagination } from 'Components/common/DefaultPagination';
import { Shimmer } from 'Components/PageLoader/styles';
import { PageLoaderList } from 'Components/PageLoader/List';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const FlexRow = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  margin-block: 16px;
  margin-inline: auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  padding: 20px;
`;

const CardTitle = styled.div`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
  margin-bottom: 16px;
`;

const TH_STYLE = {
  fontFamily: 'Maven Pro, sans-serif',
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--vo-slate)',
  textTransform: 'uppercase',
  letterSpacing: '.08em',
  padding: '11px 14px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
};

const TD_STYLE = {
  padding: '12px 14px',
  fontFamily: 'Lato, sans-serif',
  fontSize: '13px',
  color: 'var(--vo-ink)',
  verticalAlign: 'middle',
};

function HBar({ label, qt, maxQt, color, labelWidth = '90px' }) {
  const pct = maxQt > 0 ? Math.max(2, (qt / maxQt) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
      <span style={{ width: labelWidth, fontSize: '12px', fontFamily: 'Maven Pro, sans-serif', color: 'var(--vo-slate)', flexShrink: 0, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <div style={{ flex: 1, background: 'var(--vo-mist)', borderRadius: '4px', height: '20px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: '3px' }} />
      </div>
      <span style={{ fontSize: '12px', fontFamily: 'Maven Pro, sans-serif', fontWeight: 600, color: 'var(--color-brand)', minWidth: '24px' }}>{qt}</span>
    </div>
  );
}

function certExpiryStyle(validTill) {
  if (!validTill) return null;
  const daysLeft = (new Date(validTill) - new Date()) / (1000 * 60 * 60 * 24);
  if (daysLeft < 0) return { color: 'var(--vo-danger)', background: 'var(--vo-danger-10)' };
  if (daysLeft < 30) return { color: 'var(--vo-warning)', background: 'var(--vo-warning-10)' };
  return { color: 'var(--vo-success)', background: 'var(--vo-success-10)' };
}

const BADGE_BASE = {
  display: 'inline-flex',
  fontFamily: 'Maven Pro, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  padding: '3px 10px',
  borderRadius: '20px',
  whiteSpace: 'nowrap',
};

export const TabCert = () => {
  const { t } = useTranslation(['asm']);
  const [page, setPage] = useState(1);
  const selectedTenant = useSelector(useAuthTenant);

  const queries = useQueries([
    {
      queryKey: ['getCertCounter'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;
        const response = await getCertCounter(selectedTenant.slug);
        if (response?.data && response?.data?.certCounter) return response.data.certCounter;
        return 0;
      },
    },
    {
      queryKey: ['getCertList'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;
        const response = await getCertList(selectedTenant.slug);
        if (response?.data?.certs) return response.data.certs;
        return [];
      },
    },
    {
      queryKey: ['getAsmCertPaged', page],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getAsmCertPaged(selectedTenant.slug, page, 6);
        if (result?.data?.data.length > 0) return result.data;
        return { data: [], page, totalPages: 0 };
      },
    },
  ]);

  const [
    { data: certCounter, isLoading: certCounterLoading },
    { data: certs, isLoading: certsLoading },
    { data: pagedAsmCert, isLoading: asmCertLoading },
  ] = queries;

  const certMax = (certs || []).reduce((m, d) => Math.max(m, d.qt || 0), 0);

  const columns = [
    { id: 'asset', label: t('asm:tabCert.table.asset') },
    { id: 'dns_names', label: t('asm:tabCert.table.dnsNames') },
    { id: 'issuer', label: t('asm:tabCert.table.issuer') },
    { id: 'pubkey_sha256', label: t('asm:tabCert.table.publicKey') },
    { id: 'valid_till', label: t('asm:tabCert.table.validUntil') },
  ];

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'row', gap: '24px', marginBottom: '24px', alignItems: 'stretch', flexWrap: 'wrap' }} data-tour="cert-overview">
        <div
          style={{ width: '200px', flexShrink: 0, background: 'white', borderRadius: '14px', boxShadow: '0 1px 3px var(--vo-escuro-8)', padding: '24px', display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}
          data-tour="cert-counter"
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--vo-capri-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <polyline points="9 12 11 14 15 10" />
            </svg>
          </div>
          {certCounterLoading
            ? <Shimmer style={{ height: '44px', width: '80px' }} />
            : <div style={{ fontFamily: 'Rubik, sans-serif', fontSize: '44px', fontWeight: 700, color: 'var(--color-brand)', lineHeight: 1 }}>{certCounter ?? 0}</div>
          }
          <div style={{ fontFamily: 'Maven Pro, sans-serif', fontSize: '11px', fontWeight: 600, color: 'var(--vo-slate)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
            {t('asm:tabCert.cardTitle')}
          </div>
        </div>
        <Card style={{ flex: 1, minWidth: '280px' }} data-tour="cert-issuers">
          <CardTitle>{t('asm:tabCert.chartIssuers')}</CardTitle>
          {certsLoading
            ? <Shimmer style={{ height: '150px', width: '100%' }} />
            : (certs || []).map(d => <HBar key={d.name} label={d.name} qt={d.qt} maxQt={certMax} color="var(--color-brand)" labelWidth="140px" />)
          }
        </Card>
      </div>
      {asmCertLoading && <PageLoaderList />}
      {!asmCertLoading && (
        <>
          <div style={{ background: 'white', borderRadius: '14px', boxShadow: '0 1px 3px var(--vo-escuro-8)', overflow: 'auto' }} data-tour="cert-table">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
              <thead>
                <tr style={{ background: 'var(--vo-mist)' }}>
                  {columns.map(col => (
                    <th key={col.id} style={TH_STYLE}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(pagedAsmCert?.data || []).length === 0 && (
                  <tr>
                    <td colSpan={columns.length} style={{ ...TD_STYLE, textAlign: 'center', color: 'var(--vo-steel)', padding: '24px' }}>—</td>
                  </tr>
                )}
                {(pagedAsmCert?.data || []).map((row, i) => {
                  const expiryStyle = certExpiryStyle(row.valid_till);
                  const dateLabel = row.valid_till ? new Date(row.valid_till).toLocaleDateString() : null;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--vo-mist)' }}>
                      <td style={{ ...TD_STYLE, fontFamily: 'Maven Pro, sans-serif', fontWeight: 600 }}>{row.asset ?? '—'}</td>
                      <td style={{ ...TD_STYLE, fontFamily: 'Lato, sans-serif', fontSize: '12px', color: 'var(--vo-slate)', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {Array.isArray(row.dns_names) ? row.dns_names.join(', ') : (row.dns_names ?? '—')}
                      </td>
                      <td style={{ ...TD_STYLE, color: 'var(--vo-ink-2)' }}>{row.issuer ?? '—'}</td>
                      <td style={{ ...TD_STYLE, fontFamily: 'Lato, sans-serif', fontSize: '11px', color: 'var(--vo-steel)', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.pubkey_sha256 ?? '—'}
                      </td>
                      <td style={TD_STYLE}>
                        {dateLabel && expiryStyle
                          ? <span style={{ ...BADGE_BASE, ...expiryStyle }}>{dateLabel}</span>
                          : '—'
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pagedAsmCert?.totalPages > 0 && (
            <FlexRow data-tour="cert-pagination">
              <DefaultPagination
                count={pagedAsmCert.totalPages}
                onChange={(_, selectedPage) => setPage(selectedPage)}
                color="primary"
                page={page}
                showFirstButton
                showLastButton
              />
            </FlexRow>
          )}
        </>
      )}
    </>
  );
};
